export default class LeadRequestStatus {
    static RECEIVED = { value: 0, originalText: "Request created", dropdownText: null };
    static LEAD_CALL_COMPLETED = { value: 1, originalText: "Call with lead completed / bad lead", dropdownText: "Not ready to move forward" };
    static LEAD_CALL_RESCHEDULED = { value: 2, originalText: "No answer/left message", dropdownText: "No response received" };
    static SENT_TO_ADVISOR = { value: 3, originalText: "Lead information sent to advisor", dropdownText: "Pending" };
    static MATCHED = { value: 7, originalText: "Matched", dropdownText:"Matched" };
    static BAD_LEAD = { value: 8, originalText: "Bad lead", dropdownText: null };
    static TEST_USER = { value: 9, originalText: "Test user", dropdownText: null };
    static ADVISOR_DID_NOT_WIN = { value: 10, originalText: "Advisor did not win lead", dropdownText: "Not a Fit" };
    static NOT_COMPLETED = { value: 11, originalText: "Did not complete funnel", dropdownText: null };
    static ADVISOR_NOT_CHOSEN = { value: 12, originalText: "Did not book call", dropdownText: null };
    static FAKE_NUMBER = { value: 13, originalText: "Fake number", dropdownText: null };
    static RECAPTCHA_FAILED = { value: 14, originalText: "Recaptcha failed", dropdownText: null };

    static #allStatuses = Object.values(LeadRequestStatus); // Private helper for lookups

    /**
     * Returns the text description for a given request status value.
     * @param {number} value The numerical value of the request status.
     * @returns {string | undefined} The text description or undefined if not found.
     */
    static getText(value) {
        const status = this.#allStatuses.find(s => s.value === value);
        return status ? status.originalText : undefined;
    }

    /**
     * Returns the numerical value for a given request status text.
     * The comparison is case-insensitive and trims whitespace.
     * @param {string} text The text description of the request status.
     * @returns {number | undefined} The numerical value or undefined if not found.
     */
    static getValue(text) {
        const normalizedText = text.trim().toLowerCase();
        const status = this.#allStatuses.find(s => s.originalText.toLowerCase() === normalizedText);
        return status ? status.value : undefined;
    }

    /**
     * Returns the full status object (value and text) for a given value or text.
     * @param {number | string} identifier The numerical value or text description.
     * @returns {{value: number, originalText: string} | undefined} The status object or undefined.
     */
    static getStatus(identifier) {
        if (typeof identifier === 'number') {
            return this.#allStatuses.find(s => s.value === identifier);
        } else if (typeof identifier === 'string') {
            const normalizedText = identifier.trim().toLowerCase();
            return this.#allStatuses.find(s => s.originalText.toLowerCase() === normalizedText);
        }
        return undefined;
    }

    /**
     * Returns an array of all possible status objects.
     * @returns {Array<{value: number, originalText: string}>} An array of all status objects.
     */
    static getAllStatuses() {
        return [...this.#allStatuses]; // Return a copy to prevent external modification
    }

    /**
     * Returns an array of dropdown options for the request statuses.
     * Each option includes a value and a label for display.
     * @returns {Array<{value: number, label: string}>} An array of dropdown options.
     */
    static getDropdownOptions() {
        return this.#allStatuses
            .filter(status => status.dropdownText !== null)
            .map(status => ({
                value: status.value,
                label: status.dropdownText
            }));
    }
}